import { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Settings, User, HelpCircle, Info, Mail, RefreshCw, Copy, Scissors, Clipboard, ZoomIn, ZoomOut, Users, MessageSquare, Heart, X, Shield, Diamond, CreditCard, Maximize, UserPlus, Globe } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import LanguageSettings from "./LanguageSettings";
import DraggableResizableWindow from "./DraggableResizableWindow";
import { API, AuthContext } from "../App";

const MenuBar = ({ onLogout, onRefresh, onShowFriends, onShowMessages, onShowAdmin, onShowTOS, onShowHelp, onShowContact, onShowMyAccount, onShowDonate, onToggleFullscreen, isFullscreen }) => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [openMenu, setOpenMenu] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showLanguageSettings, setShowLanguageSettings] = useState(false);

  // Profile age-restrict toggle — persists to the backend via /users/profile.
  // Mirrored in the Preferences dialog so users can quickly flip it without
  // opening the full Profile editor.
  const [profileAgeRestricted, setProfileAgeRestricted] = useState(false);
  const [savingAgeRestrict, setSavingAgeRestrict] = useState(false);
  useEffect(() => {
    if (!user?.user_id || !token) return;
    axios.get(`${API}/users/${user.user_id}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setProfileAgeRestricted(!!res.data?.profile_age_restricted))
      .catch(() => { /* non-fatal */ });
  }, [user?.user_id, token]);
  const updateAgeRestrict = async (next) => {
    setSavingAgeRestrict(true);
    setProfileAgeRestricted(next); // optimistic
    try {
      await axios.put(`${API}/users/profile`, { profile_age_restricted: next }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(next ? "Profile marked 18+ only" : "18+ restriction removed");
    } catch (err) {
      setProfileAgeRestricted(!next); // revert
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSavingAgeRestrict(false);
    }
  };

  const [preferences, setPreferences] = useState({
    soundEnabled: true,
    notificationsEnabled: true,
    showTimestamps: true,
    fontSize: 'medium',
    theme: 'classic',
    autoScroll: true,
    showJoinLeave: true,
    pmSound: true,
    flashTaskbar: true,
    notificationVolume: 70  // 0-100
  });
  const menuRef = useRef(null);

  // Load preferences from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences(p => ({ ...p, ...parsed }));
      } catch (e) {
        console.error("Failed to load preferences");
      }
    }
  }, []);

  // Persist preferences live as they change (so sound utils can read latest values)
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  // Save preferences
  const savePreferences = () => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    setShowPreferences(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const menus = {
    file: {
      label: "File",
      items: [
        { label: "Refresh Rooms", icon: RefreshCw, action: onRefresh, shortcut: "F5" },
        { divider: true },
        { label: "Preferences", icon: Settings, action: () => setShowPreferences(true) },
        { divider: true },
        { label: "Sign Out", icon: LogOut, action: onLogout, highlight: true }
      ]
    },
    chat: {
      label: "Chat",
      items: [
        { label: "Friends List", icon: Users, action: onShowFriends },
        { label: "Messages", icon: MessageSquare, action: onShowMessages },
        { divider: true },
        { label: "My Profile", icon: User, action: () => navigate('/profile') },
        ...(user?.is_admin ? [
          { divider: true },
          { label: "Admin Panel", icon: Shield, action: onShowAdmin, highlight: true }
        ] : [])
      ]
    },
    account: {
      label: "Account",
      items: [
        { label: "My Account", icon: Diamond, action: onShowMyAccount },
        { label: "Edit Profile", icon: User, action: () => navigate('/profile') },
        { label: "Change Password", icon: Shield, action: () => onShowMyAccount?.('password') },
        { divider: true },
        { label: "Buy Diamonds", icon: CreditCard, action: onShowMyAccount },
        { divider: true },
        { label: "Language & Translation", icon: Globe, action: () => setShowLanguageSettings(true) },
        { divider: true },
        { label: "Donate", icon: Heart, action: onShowDonate, highlight: true }
      ]
    },
    view: {
      label: "View",
      items: [
        { label: isFullscreen ? "Exit Fullscreen" : "Fullscreen", icon: Maximize, action: onToggleFullscreen },
        { divider: true },
        { label: "Zoom In", icon: ZoomIn, action: () => document.body.style.zoom = (parseFloat(document.body.style.zoom || 1) + 0.1) },
        { label: "Zoom Out", icon: ZoomOut, action: () => document.body.style.zoom = (parseFloat(document.body.style.zoom || 1) - 0.1) },
        { label: "Reset Zoom", action: () => document.body.style.zoom = 1 }
      ]
    },
    edit: {
      label: "Edit",
      items: [
        { label: "Cut", icon: Scissors, action: () => document.execCommand('cut'), shortcut: "Ctrl+X" },
        { label: "Copy", icon: Copy, action: () => document.execCommand('copy'), shortcut: "Ctrl+C" },
        { label: "Paste", icon: Clipboard, action: () => document.execCommand('paste'), shortcut: "Ctrl+V" },
        { divider: true },
        { label: "Select All", action: () => document.execCommand('selectAll'), shortcut: "Ctrl+A" }
      ]
    },
    help: {
      label: "Help",
      items: [
        { label: "Terms of Service", icon: Info, action: onShowTOS },
        { label: "Community Rules", icon: Info, action: onShowTOS },
        { label: "Help Topics", icon: HelpCircle, action: onShowHelp },
        { divider: true },
        { label: "Contact Us", icon: Mail, action: onShowContact },
        { divider: true },
        { label: "About MEGA!", icon: Info, action: () => setShowAbout(true) }
      ]
    }
  };

  const handleMenuClick = (menuKey) => {
    setOpenMenu(openMenu === menuKey ? null : menuKey);
  };

  const handleItemClick = (item) => {
    if (!item.disabled && item.action) {
      item.action();
    }
    setOpenMenu(null);
  };

  return (
    <>
      <div 
        ref={menuRef}
        className="bg-[#C0C0C0] border-b border-[#808080] px-1 py-0.5 flex gap-0 relative"
        data-testid="menu-bar"
      >
        {Object.entries(menus).map(([key, menu]) => (
          <div key={key} className="relative">
            <button
              className={`px-3 py-1 font-arial text-xs font-semibold hover:bg-[#000080] hover:text-white ${openMenu === key ? 'bg-[#000080] text-white' : ''}`}
              onClick={() => handleMenuClick(key)}
              onMouseEnter={() => openMenu && setOpenMenu(key)}
            >
              {menu.label}
            </button>
            
            {openMenu === key && (
              <div className="absolute top-full left-0 bg-[#C0C0C0] border-2 border-t-white border-l-white border-r-[#404040] border-b-[#404040] min-w-[180px] shadow-lg z-50">
                {menu.items.map((item, idx) => {
                  if (item.divider) {
                    return <div key={idx} className="border-t border-[#808080] my-1 mx-1" />;
                  }
                  const Icon = item.icon;
                  return (
                    <button
                      key={idx}
                      className={`w-full text-left px-3 py-1.5 font-arial text-xs flex items-center gap-2 ${
                        item.disabled 
                          ? 'text-[#808080] cursor-not-allowed' 
                          : item.highlight
                            ? 'hover:bg-[#000080] hover:text-white font-bold text-[#7B0099]'
                            : 'hover:bg-[#000080] hover:text-white'
                      }`}
                      onClick={() => handleItemClick(item)}
                      disabled={item.disabled}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      <span className="flex-1">{item.label}</span>
                      {item.shortcut && (
                        <span className="text-[10px] text-[#808080]">{item.shortcut}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* About Dialog */}
      {showAbout && (
        <DraggableResizableWindow
          title={<><Info className="w-4 h-4 inline mr-2" />About MEGA!</>}
          onClose={() => setShowAbout(false)}
          initialSize={{ width: 420, height: 540 }}
          minSize={{ width: 320, height: 380 }}
          initialPosition={{ x: Math.max(40, (window.innerWidth - 420) / 2), y: 80 }}
          zIndex={9990}
          titleBarColor="bg-gradient-to-r from-[#7B0099] to-[#000080]"
        >
          <div className="p-6 bg-[#C0C0C0] text-center" data-testid="about-dialog">
              <div className="retro-inset p-4 mb-4 bg-gradient-to-b from-[#7B0099] to-[#000080]">
                <h1 className="font-bazooka text-2xl text-[#FFCC00] drop-shadow-lg mb-1" style={{fontFamily: 'Bazooka, Impact, sans-serif'}}>
                  MEGA!
                </h1>
                <p className="text-white text-sm font-bold">Chat</p>
                <p className="text-[#FFCC00] text-xs mt-1">Version 1.0</p>
              </div>
              
              <div className="space-y-3 text-sm font-arial">
                <p className="font-bold text-[#7B0099]">
                  Created by Mr_Fuker AKA Chris
                </p>
                
                {/* Scrolling Dedication Ticker */}
                <div className="text-red-600 text-center">
                  <span className="flex items-center justify-center gap-1 mb-1">
                    Dedicated to <Heart className="w-4 h-4 fill-red-600" />
                  </span>
                  <div className="overflow-hidden bg-gradient-to-r from-pink-100 to-red-100 rounded p-1 border border-red-200">
                    <div className="scrolling-ticker whitespace-nowrap animate-scroll">
                      <span className="text-xs text-red-700 font-bold">
                        ❤️ Yana ❤️ Daphne ❤️ MoM ❤️ Country ❤️ Blair ❤️ Mia ❤️ Doloris ❤️ Amanda ❤️ Bratty ❤️ Coralie ❤️ Chausie ❤️ Laya ❤️ Evelyn ❤️ Sofi ❤️ Fairycore ❤️ Banish ❤️ minnnie ❤️ xena ❤️ Cherry ❤️ Trizz ❤️ Angel ❤️ Jinx ❤️ Meowww ❤️ Xlexia ❤️ Cheeto ❤️ Barbie ❤️ Andi ❤️ Melissa ❤️ Catmom ❤️ Candice ❤️ Sugar ❤️ Jane ❤️ FknPrincez ❤️ Jade ❤️ Hanna ❤️ fire ❤️ Venis ❤️ Rhonda ❤️ Effey ❤️ Tina ❤️ Sharon ❤️ Charlotte ❤️ Judy ❤️ Alex ❤️ Sara ❤️ Catherine ❤️ Tinkerbell ❤️ Blossom ❤️ Amber ❤️ Kerry ❤️ Lisa ❤️ Linda ❤️ Yevonne ❤️ Yana ❤️ Daphne ❤️ MoM ❤️ Country ❤️ Blair ❤️ Mia ❤️ Doloris ❤️ Amanda ❤️ Bratty ❤️ Coralie ❤️ Chausie ❤️ Laya ❤️ Evelyn ❤️ Sofi ❤️ Fairycore ❤️ Banish ❤️ minnnie ❤️ xena ❤️ Cherry ❤️ Trizz ❤️ Angel ❤️ Jinx ❤️ Meowww ❤️ Xlexia ❤️ Cheeto ❤️ Barbie ❤️ Andi ❤️ Melissa ❤️ Catmom ❤️ Candice ❤️ Sugar ❤️ Jane ❤️ FknPrincez ❤️ Jade ❤️ Hanna ❤️ fire ❤️ Venis ❤️ Rhonda ❤️ Effey ❤️ Tina ❤️ Sharon ❤️ Charlotte ❤️ Judy ❤️ Alex ❤️ Sara ❤️ Catherine ❤️ Tinkerbell ❤️ Blossom ❤️ Amber ❤️ Kerry ❤️ Lisa ❤️ Linda ❤️ Yevonne ❤️
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-[#666] text-xs leading-relaxed">
                  & the people who pushed me to<br />
                  make a quality chat room<br />
                  that feels like old school chat<br />
                  but with modern upgrades
                </p>
                
                <div className="border-t border-[#808080] pt-3 mt-3">
                  <p className="font-bold text-[#000080]">A New Chat Experience!</p>
                  <p className="text-[#7B0099] italic">with an old school feel!</p>
                </div>
              </div>
              
              <button 
                className="retro-button px-8 py-2 mt-4 font-bold"
                onClick={() => setShowAbout(false)}
              >
                OK
              </button>
          </div>
        </DraggableResizableWindow>
      )}

      {/* Preferences Dialog */}
      {showPreferences && (
        <DraggableResizableWindow
          title={<><Settings className="w-4 h-4 inline mr-2" />Preferences</>}
          onClose={() => setShowPreferences(false)}
          initialSize={{ width: 480, height: 580 }}
          minSize={{ width: 360, height: 400 }}
          initialPosition={{ x: Math.max(40, (window.innerWidth - 480) / 2), y: 60 }}
          zIndex={9991}
          titleBarColor="bg-gradient-to-r from-[#7B0099] to-[#000080]"
        >
          <div className="bg-[#C0C0C0] h-full overflow-auto" data-testid="preferences-dialog">
            <div className="p-4">
              <div className="retro-inset p-4 mb-4">
                {/* Sound Settings */}
                <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2 border-b pb-1">Sound Settings</h3>
                <div className="space-y-2 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.soundEnabled}
                      onChange={(e) => setPreferences(p => ({ ...p, soundEnabled: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Enable sound effects</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.pmSound}
                      onChange={(e) => setPreferences(p => ({ ...p, pmSound: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Play sound on new PM</span>
                  </label>

                  {/* Notification volume slider */}
                  <div className="pt-2">
                    <label className="font-arial text-xs flex items-center justify-between mb-1">
                      <span>🔊 Notification volume</span>
                      <span className="font-bold tabular-nums" data-testid="volume-value">
                        {preferences.notificationVolume ?? 70}%
                      </span>
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={preferences.notificationVolume ?? 70}
                      onChange={(e) => setPreferences(p => ({ ...p, notificationVolume: parseInt(e.target.value, 10) }))}
                      className="w-full"
                      disabled={!preferences.soundEnabled}
                      data-testid="notification-volume-slider"
                    />
                    <p className="font-arial text-[10px] text-[#666] mt-1">
                      Controls volume for IM dings, friend online/offline pings, and welcome chime.
                    </p>
                  </div>
                </div>

                {/* Notification Settings */}
                <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2 border-b pb-1">Notifications</h3>
                <div className="space-y-2 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.notificationsEnabled}
                      onChange={(e) => setPreferences(p => ({ ...p, notificationsEnabled: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Enable notifications</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.flashTaskbar}
                      onChange={(e) => setPreferences(p => ({ ...p, flashTaskbar: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Flash taskbar on new message</span>
                  </label>
                </div>

                {/* Privacy / Profile Restrictions */}
                <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2 border-b pb-1">Privacy</h3>
                <div className="space-y-2 mb-4 bg-red-50 border-2 border-red-300 rounded p-2" data-testid="prefs-age-restrict-section">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={profileAgeRestricted}
                      disabled={savingAgeRestrict}
                      onChange={(e) => updateAgeRestrict(e.target.checked)}
                      className="w-4 h-4 mt-1 accent-red-600"
                      data-testid="prefs-age-restrict-checkbox"
                    />
                    <div className="flex-1">
                      <div className="font-arial text-xs font-bold text-red-700 flex items-center gap-1">
                        🔞 Age-Restrict My Profile (18+ Only)
                        <button
                          type="button"
                          className="ml-1 w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center hover:bg-blue-700"
                          title="Why this matters"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.alert(
                              "IMPORTANT — SITE POLICY\n\n" +
                              "If your profile contains ANY adult content or nudity (photos, videos, " +
                              "explicit bio, suggestive avatar, etc.) you MUST turn this on.\n\n" +
                              "Failure to age-restrict adult or nude content will result in your account " +
                              "being permanently BANNED from the site.\n\n" +
                              "When this is on:\n" +
                              "• Only users 18+ who have purchased the Adult Pass can view your profile, " +
                              "photos, and videos.\n" +
                              "• Admins and moderators can always view for moderation purposes.\n" +
                              "• You can still see your own profile normally."
                            );
                          }}
                          data-testid="prefs-age-restrict-info-btn"
                        >
                          i
                        </button>
                      </div>
                      <p className="text-[10px] text-zinc-700 mt-0.5 leading-snug">
                        Hides your profile, photos &amp; videos from anyone under 18 / without the Adult Pass.
                        <strong className="text-red-700"> Required if your profile has adult content or nudity.</strong>
                      </p>
                    </div>
                  </label>
                </div>

                {/* Chat Settings */}
                <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2 border-b pb-1">Chat Display</h3>
                <div className="space-y-2 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.showTimestamps}
                      onChange={(e) => setPreferences(p => ({ ...p, showTimestamps: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Show timestamps</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.autoScroll}
                      onChange={(e) => setPreferences(p => ({ ...p, autoScroll: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Auto-scroll to new messages</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.showJoinLeave}
                      onChange={(e) => setPreferences(p => ({ ...p, showJoinLeave: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="font-arial text-xs">Show join/leave messages</span>
                  </label>
                </div>

                {/* Font Size */}
                <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2 border-b pb-1">Font Size</h3>
                <div className="mb-4">
                  <select
                    value={preferences.fontSize}
                    onChange={(e) => setPreferences(p => ({ ...p, fontSize: e.target.value }))}
                    className="retro-input w-full text-xs"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium (Default)</option>
                    <option value="large">Large</option>
                  </select>
                </div>

                {/* Theme */}
                <h3 className="font-verdana text-sm font-bold text-[#7B0099] mb-2 border-b pb-1">Theme</h3>
                <div className="mb-4">
                  <select
                    value={preferences.theme}
                    onChange={(e) => setPreferences(p => ({ ...p, theme: e.target.value }))}
                    className="retro-input w-full text-xs"
                  >
                    <option value="classic">Classic (Default)</option>
                    <option value="dark">Dark Mode</option>
                    <option value="light">Light Mode</option>
                    <option value="purple">Purple Haze</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <button 
                  className="retro-button flex-1 py-2 bg-[#008000] text-white border-[#008000] font-bold"
                  onClick={savePreferences}
                >
                  Save
                </button>
                <button 
                  className="retro-button flex-1 py-2"
                  onClick={() => setShowPreferences(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </DraggableResizableWindow>
      )}

      {/* Language Settings Modal */}
      {showLanguageSettings && (
        <DraggableResizableWindow
          title={<><Globe className="w-4 h-4 inline mr-2" />Language & Translation</>}
          onClose={() => setShowLanguageSettings(false)}
          initialSize={{ width: 480, height: 540 }}
          minSize={{ width: 360, height: 400 }}
          initialPosition={{ x: Math.max(40, (window.innerWidth - 480) / 2), y: 80 }}
          zIndex={9999}
          titleBarColor="bg-gradient-to-r from-blue-600 to-indigo-600"
        >
          <div className="bg-[#C0C0C0] h-full overflow-auto">
            <LanguageSettings onClose={() => setShowLanguageSettings(false)} />
          </div>
        </DraggableResizableWindow>
      )}
    </>
  );
};

export default MenuBar;
